/**
 * Image Format Converter Component
 * Convert images between different formats
 */

import { memo, useState, useCallback } from 'react';
import { useToast } from '../../context';
import { 
  convertImageFormat, 
  formatFileSize,
  FORMAT_INFO,
  type ImageFormat,
  type ConversionResult 
} from '../../services/imageConverterService';
import { analytics } from '../../services/analyticsService';

// Icons
const UploadIcon = memo(() => (
  <svg className="w-12 h-12 mx-auto mb-4 text-indigo-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
  </svg>
));
UploadIcon.displayName = 'UploadIcon';

const DownloadIcon = memo(() => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
  </svg>
));
DownloadIcon.displayName = 'DownloadIcon';

const SpinnerIcon = memo(() => (
  <svg className="w-8 h-8 animate-spin text-indigo-400" fill="none" viewBox="0 0 24 24">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
  </svg>
));
SpinnerIcon.displayName = 'SpinnerIcon';

const ArrowIcon = memo(() => (
  <svg className="w-6 h-6 text-indigo-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
  </svg>
));
ArrowIcon.displayName = 'ArrowIcon';

const OUTPUT_FORMATS: ImageFormat[] = ['png', 'jpeg', 'webp', 'ico', 'bmp'];

export const ImageConverter = memo(function ImageConverter() {
  const [isDragging, setIsDragging] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [originalImage, setOriginalImage] = useState<string | null>(null);
  const [originalFile, setOriginalFile] = useState<File | null>(null);
  const [result, setResult] = useState<ConversionResult | null>(null);
  const [targetFormat, setTargetFormat] = useState<ImageFormat>('png');
  const [quality, setQuality] = useState(90);
  const [bgColor, setBgColor] = useState('#ffffff');
  const { success, error, info } = useToast();

  const processFile = useCallback(async (file: File) => {
    if (!file.type.startsWith('image/')) {
      error('Please upload an image file');
      return;
    }

    setIsProcessing(true);
    info('Converting image...');

    try {
      // Store original
      const reader = new FileReader();
      reader.onload = (e) => setOriginalImage(e.target?.result as string);
      reader.readAsDataURL(file);
      setOriginalFile(file);

      // Convert
      const converted = await convertImageFormat(file, {
        format: targetFormat,
        quality: quality / 100,
        backgroundColor: bgColor,
      });

      setResult(converted);
      success(`Converted to ${FORMAT_INFO[targetFormat].name}!`);
      analytics.convert.convert(file.type, targetFormat);
    } catch (err) {
      error('Failed to convert image');
      console.error(err);
    } finally {
      setIsProcessing(false);
    }
  }, [targetFormat, quality, bgColor, success, error, info]);

  const handleReconvert = useCallback(async () => {
    if (originalFile) {
      await processFile(originalFile);
    }
  }, [originalFile, processFile]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) processFile(file);
  }, [processFile]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleFileInput = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) processFile(file);
  }, [processFile]);

  const handleDownload = useCallback(() => {
    if (!result || !originalFile) return;
    
    const url = URL.createObjectURL(result.blob);
    const link = document.createElement('a');
    link.href = url;
    const baseName = originalFile.name.replace(/\.[^/.]+$/, '');
    link.download = `${baseName}${FORMAT_INFO[result.format].extension}`;
    link.click();
    URL.revokeObjectURL(url);
    success('Downloaded!');
    analytics.convert.download(result.format);
  }, [result, originalFile, success]);

  const handleClear = useCallback(() => {
    setOriginalImage(null);
    setOriginalFile(null);
    setResult(null);
  }, []);

  const formatInfo = FORMAT_INFO[targetFormat];

  return (
    <div className="space-y-6">
      {/* Format Selection */}
      <div className="bg-slate-800 rounded-xl border border-slate-700 p-4">
        <h3 className="text-sm font-medium text-slate-300 mb-4">Output Format</h3>
        <div className="flex flex-wrap gap-2">
          {OUTPUT_FORMATS.map((f) => (
            <button
              key={f}
              onClick={() => setTargetFormat(f)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                targetFormat === f
                  ? 'bg-indigo-600 text-white'
                  : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
              }`}
            >
              {FORMAT_INFO[f].name}
            </button>
          ))}
        </div>

        {/* Format-specific options */}
        <div className="mt-4 grid md:grid-cols-2 gap-4">
          {/* Quality slider for lossy formats */}
          {formatInfo.lossy && (
            <div>
              <label className="block text-xs text-slate-400 mb-2">Quality: {quality}%</label>
              <input
                type="range"
                min="10"
                max="100"
                value={quality}
                onChange={(e) => setQuality(Number(e.target.value))}
                className="w-full accent-indigo-500"
              />
            </div>
          )}

          {/* Background color for non-transparent formats */}
          {!formatInfo.supportsTransparency && (
            <div>
              <label className="block text-xs text-slate-400 mb-2">Background Color</label>
              <div className="flex gap-2">
                <input
                  type="color"
                  value={bgColor}
                  onChange={(e) => setBgColor(e.target.value)}
                  className="w-10 h-10 rounded cursor-pointer"
                />
                <input
                  type="text"
                  value={bgColor}
                  onChange={(e) => setBgColor(e.target.value)}
                  className="flex-1 bg-slate-700 border border-slate-600 rounded-lg px-3 text-sm text-white"
                />
              </div>
            </div>
          )}
        </div>

        {/* Format info badges */}
        <div className="mt-4 flex flex-wrap gap-2">
          {formatInfo.supportsTransparency && (
            <span className="text-xs bg-blue-500/20 text-blue-400 px-2 py-1 rounded">
              ✓ Transparency
            </span>
          )}
          {formatInfo.lossy ? (
            <span className="text-xs bg-yellow-500/20 text-yellow-400 px-2 py-1 rounded">
              Lossy compression
            </span>
          ) : (
            <span className="text-xs bg-green-500/20 text-green-400 px-2 py-1 rounded">
              Lossless
            </span>
          )}
        </div>

        {originalFile && (
          <button
            onClick={handleReconvert}
            disabled={isProcessing}
            className="mt-4 px-4 py-2 bg-indigo-600 hover:bg-indigo-500 rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
          >
            Apply New Settings
          </button>
        )}
      </div>

      {/* Drop Zone */}
      <div
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        className={`
          relative rounded-xl border-2 border-dashed p-8 text-center transition-all cursor-pointer
          ${isDragging ? 'border-indigo-500 bg-indigo-500/10' : 'border-slate-600 hover:border-indigo-400 bg-slate-800/50'}
          ${isProcessing ? 'pointer-events-none opacity-50' : ''}
        `}
      >
        <input
          type="file"
          accept="image/*"
          onChange={handleFileInput}
          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
          disabled={isProcessing}
        />
        
        {isProcessing ? (
          <div className="py-4">
            <SpinnerIcon />
            <p className="mt-4 text-slate-400">Converting...</p>
          </div>
        ) : (
          <>
            <UploadIcon />
            <p className="text-slate-300 font-medium">Drop your image here</p>
            <p className="text-slate-500 text-sm mt-1">PNG, JPG, WebP, GIF, BMP, or SVG</p>
          </>
        )}
      </div>

      {/* Results */}
      {result && (
        <>
          <div className="bg-slate-800 rounded-xl border border-slate-700 p-4">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-4">
                <div className="text-center">
                  <span className="text-xs text-slate-500 block">From</span>
                  <span className="text-lg font-medium text-white">
                    {originalFile?.type.split('/')[1].toUpperCase()}
                  </span>
                </div>
                <ArrowIcon />
                <div className="text-center">
                  <span className="text-xs text-slate-500 block">To</span>
                  <span className="text-lg font-medium text-indigo-400">
                    {FORMAT_INFO[result.format].name}
                  </span>
                </div>
              </div>
              <button
                onClick={handleDownload}
                className="flex items-center gap-2 px-6 py-2.5 bg-indigo-600 hover:bg-indigo-500 rounded-lg font-medium transition-colors"
              >
                <DownloadIcon />
                Download
              </button>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              {/* Original */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-slate-400">Original</span>
                  <span className="text-xs text-slate-500">{originalFile && formatFileSize(originalFile.size)}</span>
                </div>
                <div className="bg-slate-900 rounded-lg p-4 flex items-center justify-center min-h-[200px]">
                  {originalImage && (
                    <img src={originalImage} alt="Original" className="max-w-full max-h-[200px] object-contain" />
                  )}
                </div>
              </div>

              {/* Converted */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-slate-400">Converted</span>
                  <span className="text-xs text-slate-500">{formatFileSize(result.size)}</span>
                </div>
                <div className="bg-slate-900 rounded-lg p-4 flex items-center justify-center min-h-[200px]">
                  <img src={result.dataUrl} alt="Converted" className="max-w-full max-h-[200px] object-contain" />
                </div>
              </div>
            </div>

            <div className="mt-4 text-center text-sm text-slate-500">
              {result.width} × {result.height} pixels
            </div>
          </div>

          {/* Clear Button */}
          <div className="text-center">
            <button
              onClick={handleClear}
              className="px-6 py-2.5 text-sm font-medium bg-slate-700 hover:bg-slate-600 text-white rounded-lg border border-slate-600 hover:border-slate-500 transition-all"
            >
              Convert Another Image
            </button>
          </div>
        </>
      )}

      {/* Info */}
      <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700">
        <h4 className="text-sm font-medium text-slate-300 mb-2">📋 Format Guide:</h4>
        <div className="grid md:grid-cols-2 gap-2 text-sm text-slate-400">
          <div><strong className="text-white">PNG:</strong> Lossless, transparency, best for graphics</div>
          <div><strong className="text-white">JPEG:</strong> Lossy, small size, best for photos</div>
          <div><strong className="text-white">WebP:</strong> Modern, small size, transparency support</div>
          <div><strong className="text-white">ICO:</strong> Favicon format for websites</div>
          <div><strong className="text-white">BMP:</strong> Uncompressed bitmap format</div>
        </div>
      </div>
    </div>
  );
});

export default ImageConverter;
